import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import { listNotifications, markNotificationRead, markAllNotificationsRead } from '../api.js';

function timeAgo(dateString) {
  const seconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function NotificationDropdown({ anchorRef, onClose }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    listNotifications()
      .then((data) => { if (!cancelled) setNotifications(data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const handler = (event) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target) &&
        anchorRef.current && !anchorRef.current.contains(event.target)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [anchorRef, onClose]);

  const onClickNotification = async (notification) => {
    if (!notification.read) {
      try { await markNotificationRead(notification.notification_id); } catch { /* best-effort */ }
    }
    onClose();
    navigate(notification.link);
  };

  const onMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch { /* best-effort */ }
  };

  const hasUnread = notifications.some((n) => !n.read);

  return (
    <div className="notification-dropdown" ref={dropdownRef}>
      <div className="notification-dropdown-header">
        <span className="notification-dropdown-title">Notifications</span>
        {hasUnread && (
          <button type="button" className="notification-mark-all" onClick={onMarkAllRead}>
            Mark all as read
          </button>
        )}
      </div>
      {loading && <div className="notification-empty">Loading…</div>}
      {!loading && notifications.length === 0 && (
        <div className="notification-empty">No notifications</div>
      )}
      {!loading && notifications.map((n) => (
        <button
          key={n.notification_id}
          type="button"
          className={`notification-item${n.read ? '' : ' unread'}`}
          onClick={() => onClickNotification(n)}
        >
          <div className="notification-item-title">{n.title}</div>
          <div className="notification-item-body">{n.body}</div>
          <div className="notification-item-meta">
            <span className="notification-item-record">{n.record_name}</span>
            {n.created_at && <span className="notification-item-time">{timeAgo(n.created_at)}</span>}
          </div>
        </button>
      ))}
    </div>
  );
}
